import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { notificationsApi } from './api';

const KEY = ['notifications'] as const;

/**
 * Unread count for the app-shell bell (issue #103).
 *
 * Deliberately its own query rather than reusing useNotifications(): the shell
 * renders on every page, and we only need the number. The backend returns
 * `unread_count` whatever the page size (backend/messaging/views.py:725), so
 * this asks for a single row. Polls so a notification that arrives mid-session
 * lights the bell without a reload.
 */
export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: [...KEY, 'unread-count'] as const,
    queryFn: () => notificationsApi.list({ unread_only: true, limit: 1 }),
    select: (data) => data.unreadCount,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useNotifications(unreadOnly = false) {
  return useQuery({
    queryKey: [...KEY, { unreadOnly }],
    queryFn: () => notificationsApi.list({ unread_only: unreadOnly, limit: 50 }),
    staleTime: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast.success('All notifications marked as read');
    },
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificationsApi.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useClearReadNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.clearRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast.success('Cleared read notifications');
    },
  });
}
